import React from "react";
import { Activity, ChevronRight, ArrowUpRight } from "lucide-react";
import { ActivityEvent, NextStep } from "../Types/CandidateIntelligenceTypes";
import { Section, EmptyState, formatDateTime, formatRelative } from "./Section";

interface TimelineSectionProps {
  events: ActivityEvent[];
  steps: NextStep[];
}

const typeDot = (type: string | null): { label: string; cls: string } => {
  const t = type?.toUpperCase();
  if (t === "VIEWED" || t === "PROFILE_VIEWED") return { label: "Viewed", cls: "bg-cyan-400 border-cyan-500/60 text-cyan-300" };
  if (t === "DISCOVERED" || t === "DISCOVERED_IN_SEARCH") return { label: "Discovered", cls: "bg-indigo-400 border-indigo-500/60 text-indigo-300" };
  if (t === "INVITED" || t === "JOB_INVITED") return { label: "Invited", cls: "bg-amber-400 border-amber-500/60 text-amber-300" };
  if (t === "ACCEPTED" || t === "ACCEPTED_BY_RECRUITER") return { label: "Accepted", cls: "bg-emerald-400 border-emerald-500/60 text-emerald-300" };
  if (t === "REJECTED" || t === "REJECTED_BY_RECRUITER") return { label: "Declined", cls: "bg-rose-400 border-rose-500/60 text-rose-300" };
  if (t === "SEARCHED" || t === "PROFILE_SEARCHED") return { label: "Searched", cls: "bg-violet-400 border-violet-500/60 text-violet-300" };
  return { label: "Activity", cls: "bg-slate-400 border-slate-500/60 text-slate-300" };
};

/* Compact static mini-timeline shown above the activity table. */
const MiniTimeline: React.FC<{ events: ActivityEvent[] }> = ({ events }) => {
  const segments = (() => {
    const buckets: Record<string, number> = {};
    for (const e of events) {
      const d = e.createdAt ? new Date(e.createdAt) : null;
      if (!d || Number.isNaN(d.getTime())) continue;
      const key = d.toISOString().slice(0, 10);
      buckets[key] = (buckets[key] || 0) + 1;
    }
    const sorted = Object.entries(buckets).sort((a, b) => (a[0] < b[0] ? -1 : 1));
    if (sorted.length <= 1) return [];
    return sorted;
  })();

  if (segments.length === 0) return null;
  const max = Math.max(...segments.map(([, c]) => c));
  const range = segments.map(([d]) => d.slice(5)).join(" → ");

  return (
    <div className="mb-4">
      <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">{range}</p>
      <div className="flex h-16 items-end gap-1">
        {segments.map(([d, c], i) => {
          const pct = (c / max) * 100;
          const isLast = i === segments.length - 1;
          return (
            <button
              key={d}
              type="button"
              title={`${d}: ${c} events`}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className={`group flex min-w-0 max-w-12 flex-1 flex-col items-center gap-1 ${isLast ? "cursor-default" : ""}`}
            >
              <div className="flex w-full flex-1 items-end rounded-t-md bg-slate-800/80 transition group-hover:bg-cyan-500/20">
                <div
                  className={`w-full rounded-t-md ${isLast ? "bg-cyan-500/70" : "bg-slate-700"}`}
                  style={{ height: `${Math.max(pct, 8)}%` }}
                />
              </div>
              <span className={`truncate text-[9px] ${isLast ? "font-bold text-cyan-300" : "text-slate-500"}`}>
                {d.slice(5)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const TimelineSection: React.FC<TimelineSectionProps> = ({ events, steps }) => {
  const visible = events.slice(0, 10);

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
      <Section
        className="xl:col-span-2"
        icon={<Activity size={17} />}
        title="Activity Intelligence"
        subtitle="Your recent engagement — safe, interaction-based insights"
      >
        <MiniTimeline events={events} />

        {visible.length === 0 ? (
          <EmptyState label="No activity yet — when recruiters engage, it shows up here" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[540px] text-left">
              <thead>
                <tr className="border-b border-slate-800 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-2 pb-2">Type</th>
                  <th className="px-2 pb-2">Category</th>
                  <th className="px-2 pb-2">Details</th>
                  <th className="px-2 pb-2 text-right">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/70">
                {visible.map((e, i) => {
                  const dot = typeDot(e.eventType);
                  return (
                    <tr key={e.id ?? i} className="text-sm transition hover:bg-slate-900/40">
                      <td className="px-2 py-2.5">
                        <span className={`flex items-center gap-2 rounded-lg border px-2 py-1 text-[11px] font-bold ${dot.cls}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${dot.cls.split(" ")[0]}`} />
                          {dot.label}
                        </span>
                      </td>
                      <td className="px-2 py-2.5 text-xs text-slate-400">{e.category ?? "—"}</td>
                      <td className="px-2 py-2.5 text-xs text-slate-300">
                        <p className="max-w-md truncate">{e.message ?? "—"}</p>
                      </td>
                      <td className="px-2 py-2.5 text-right">
                        <span className="block text-xs text-slate-400">{e.createdAt ? formatDateTime(e.createdAt) : "—"}</span>
                        <span className="block text-[10px] text-slate-600">{e.createdAt ? formatRelative(e.createdAt) : ""}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {events.length > 10 ? (
              <button
                type="button"
                className="mt-3 flex w-full items-center justify-center gap-1 rounded-xl border border-slate-800 bg-slate-900/40 py-2 text-xs font-semibold text-slate-400 transition hover:text-cyan-300"
              >
                View all {events.length} activities <ChevronRight size={14} />
              </button>
            ) : null}
          </div>
        )}
      </Section>

      <Section
        icon={<ArrowUpRight size={17} />}
        title="Recommended next steps"
        subtitle="Highest-impact actions first"
      >
        {steps.length === 0 ? (
          <EmptyState label="No next steps right now — you're all caught up" />
        ) : (
          <ol className="flex flex-col gap-2.5">
            {steps.slice(0, 5).map((s, idx) => {
              const priorityCls =
                s.priority === "HIGH"
                  ? "text-rose-300 bg-rose-500/10 border-rose-500/30"
                  : s.priority === "MEDIUM"
                    ? "text-amber-300 bg-amber-500/10 border-amber-500/30"
                    : "text-emerald-300 bg-emerald-500/10 border-emerald-500/30";
              return (
                <li key={s.id} className="rounded-xl border border-slate-800 bg-slate-900/50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-black text-slate-500">#{idx + 1}</span>
                    <span className={`rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${priorityCls}`}>
                      {s.priority ?? "—"}
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-bold text-slate-100">{s.title}</p>
                  <p className="mt-0.5 text-xs text-slate-400">{s.description}</p>
                  {s.section ? <p className="mt-1 text-[10px] text-slate-500">Section: {s.section}</p> : null}
                </li>
              );
            })}
          </ol>
        )}
      </Section>
    </div>
  );
};